import React, { useState, useEffect, useRef } from 'react';

const SearchableSelect = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Seçiniz veya arayınız...", 
  disabled = false,
  className = "",
  showAllOption = true,
  allOptionLabel = "Tümü",
  allOptionValue = "all"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);

  // Dışarı tıklandığında dropdown'u kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Seçenekleri filtrele
  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter(option => 
        option.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  // Seçilen değerin label'ını bul
  const getSelectedLabel = () => {
    if (value === allOptionValue) return allOptionLabel;
    // Eğer value, options listesinde yoksa direkt value'yu göster (manuel girilmiş)
    if (value && !options.includes(value) && value !== allOptionValue) {
      return value;
    }
    return value || placeholder;
  };

  const handleSelect = (option) => {
    onChange(option);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    // Eğer kullanıcı yazıyorsa, yazdığı değeri gönder
    if (newValue) {
      onChange(newValue);
    } else {
      onChange(allOptionValue);
    }
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          type="text"
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-10 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'
          }`}
          placeholder={placeholder}
          value={isOpen ? searchTerm : getSelectedLabel()}
          onChange={handleSearchChange}
          onClick={handleInputClick}
          disabled={disabled}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 flex items-center px-2"
          onClick={handleInputClick}
          disabled={disabled}
        >
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {showAllOption && (
            <div
              className={`px-3 py-2 cursor-pointer hover:bg-indigo-50 ${
                value === allOptionValue ? 'bg-indigo-100 text-indigo-700 font-medium' : ''
              }`}
              onClick={() => handleSelect(allOptionValue)}
            >
              {allOptionLabel}
            </div>
          )}
          
          {/* Arama terimi varsa ve listede yoksa özel seçenek göster */}
          {searchTerm && !filteredOptions.includes(searchTerm) && (
            <div
              className="px-3 py-2 cursor-pointer hover:bg-green-50 bg-green-100 text-green-700 font-medium border-b border-green-200"
              onClick={() => handleSelect(searchTerm)}
            >
              <div className="flex items-center justify-between">
                <span>✨ "{searchTerm}" kullan</span>
                <span className="text-xs text-green-600">(Yeni giriş)</span>
              </div>
            </div>
          )}
          
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                className={`px-3 py-2 cursor-pointer hover:bg-indigo-50 ${
                  value === option ? 'bg-indigo-100 text-indigo-700 font-medium' : ''
                }`}
                onClick={() => handleSelect(option)}
              >
                <div className="flex items-center justify-between">
                  <span>{option}</span>
                  {option.toLowerCase().includes('bank') && (
                    <span className="text-xs text-gray-500">🏦</span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-gray-500 text-center">
              {!searchTerm && 'Seçenek bulunamadı'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;